import {useCallback, useId, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import clsx from "clsx";
import {FileText, RefreshCw, WifiOff} from "lucide-react";
import {useCapabilities} from "../../../shared/auth/CapabilitiesContext";
import {DataState} from "../../../shared/components/DataState";
import {PageToolbar, ToolbarSelect} from "../../../shared/components/PageToolbar";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {OFFLINE_COPY, OFFLINE_TITLE, defaultCountryId} from "../appTextsContracts";
import {countryDocumentsKeys, loadCountryDocuments} from "../countryDocumentsApi";
import {
    canEditCountryDocuments,
    documentCountry,
    documentKindLabel,
    documentKinds,
    documentSummary,
    documentsErrorMessage,
} from "../countryDocumentsContracts";
import {ConfirmDialog} from "./ConfirmDialog";
import {CountryDocumentEditor} from "./CountryDocumentEditor";
import {useTranslation, i18n} from "../../../shared/i18n";


const EMPTY = [];

const Loading = () => (
    <div aria-label={i18n.t("sections.content.p214")} className="content-media-loading content-texts-loading">
        <span /><span /><span />
    </div>
);

// Safe for an id attribute and an aria reference.
const idPart = (value) => String(value).replace(/[^A-Za-z0-9_-]/g, "_");

export const CountryDocumentsWorkspace = ({headerCountryScope, onDirtyChange}) => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const idPrefix = idPart(useId());
    const listQuery = useQuery({
        queryKey: countryDocumentsKeys.list(),
        queryFn: loadCountryDocuments,
    });
    const list = listQuery.data;
    const countries = Array.isArray(list?.countries) ? list.countries : EMPTY;
    const kinds = documentKinds(list);

    const [countryChoice, setCountryChoice] = useState(null);
    const [kindChoice, setKindChoice] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState(null);

    // The tab keeps its own country choice: switching the header scope would remount the page.
    const chosenCountry = countries.find((country) => String(country.id) === String(countryChoice ?? ""));
    const countryId = chosenCountry ? chosenCountry.id : defaultCountryId(list, headerCountryScope);
    const country = documentCountry(list, countryId);
    const kind = kinds.some((item) => item.kind === kindChoice) ? kindChoice : kinds[0].kind;
    const kindItem = kinds.find((item) => item.kind === kind);
    const canManage = canEditCountryDocuments({list, hasAction: capabilities.hasAction});
    const tabId = (value) => `${idPrefix}-document-tab-${idPart(value)}`;
    const panelId = `${idPrefix}-document-panel`;

    const handleDirtyChange = useCallback((value) => {
        setDirty(value);
        onDirtyChange?.(value);
    }, [onDirtyChange]);

    const guard = (action) => {
        if (dirty) setPendingSwitch(() => action);
        else action();
    };
    const cancelSwitch = useCallback(() => setPendingSwitch(null), []);
    const confirmSwitch = () => {
        const action = pendingSwitch;
        setPendingSwitch(null);
        action?.();
    };
    const selectCountry = (value) => {
        if (String(value) === String(countryId ?? "")) return;
        guard(() => setCountryChoice(value));
    };
    const selectKind = (value) => {
        if (value === kind) return;
        guard(() => setKindChoice(value));
    };

    const head = (
        <div className="content-workspace-head">
            <div>
                <h2>{t("sections.content.p29")}</h2>
                <p>{t("sections.content.p215")}</p>
            </div>
        </div>
    );

    // isPending, not isLoading: a request paused by an offline browser is neither loading
    // nor failed, and must not fall through to «Нет стран в вашем доступе».
    if (listQuery.isPending) {
        if (listQuery.fetchStatus === "paused") {
            return (
                <div className="content-workspace content-documents">
                    {head}
                    <div className="content-readonly-note"><WifiOff size={16} /><span>{OFFLINE_TITLE}. {OFFLINE_COPY}</span></div>
                </div>
            );
        }
        return <div className="content-workspace content-documents">{head}<Loading /></div>;
    }
    if (listQuery.isError) {
        const notDeployed = listQuery.error?.response?.status === 404;
        return (
            <div className="content-workspace content-documents">
                {head}
                <div className="content-texts-state">
                    <DataState
                        title={notDeployed ? t("sections.content.p162") : t("sections.content.p216")}
                        copy={notDeployed ? t("sections.content.p217") : documentsErrorMessage(listQuery.error)}
                    />
                    {notDeployed ? null : <button className="admin-row-action" onClick={() => listQuery.refetch()} type="button"><RefreshCw size={14} /> {t("sections.content.p70")}</button>}
                </div>
            </div>
        );
    }
    if (!countries.length) {
        return <div className="content-workspace content-documents">{head}<DataState title={t("sections.content.p165")} copy={t("sections.content.p218")} /></div>;
    }

    const label = documentKindLabel(kind, kindItem?.title);

    return (
        <div className="content-workspace content-documents">
            {head}

            <PageToolbar>
                <ToolbarSelect
                    aria-label={t("sections.content.p219")}
                    disabled={countries.length <= 1}
                    label={t("sections.content.p170")}
                    onChange={(event) => selectCountry(event.target.value)}
                    value={String(countryId ?? "")}
                >
                    {countries.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
                </ToolbarSelect>
            </PageToolbar>

            <div aria-label={t("sections.content.p220")} className="content-documents-tabs" role="tablist">
                {kinds.map((item) => {
                    const selected = item.kind === kind;
                    const summary = documentSummary(list, countryId, item.kind);
                    return (
                        <button
                            aria-controls={selected ? panelId : undefined}
                            aria-selected={selected}
                            className={clsx("content-documents-tab", selected && "is-active")}
                            id={tabId(item.kind)}
                            key={item.kind}
                            onClick={() => selectKind(item.kind)}
                            role="tab"
                            type="button"
                        >
                            <FileText aria-hidden="true" size={15} />
                            <span>{documentKindLabel(item.kind, item.title)}</span>
                            {summary?.is_empty ? <StatusBadge tone="warning">{t("sections.content.p221")}</StatusBadge> : null}
                        </button>
                    );
                })}
            </div>

            {countryId !== null && countryId !== undefined ? (
                <CountryDocumentEditor
                    canManage={canManage}
                    countryId={countryId}
                    countryName={country?.name}
                    key={`${countryId}:${kind}`}
                    kind={kind}
                    label={label}
                    onDirtyChange={handleDirtyChange}
                    panelId={panelId}
                    tabId={tabId(kind)}
                />
            ) : null}

            <ConfirmDialog
                cancelLabel={t("sections.content.p30")}
                confirmLabel={t("sections.content.p31")}
                copy={t("sections.content.p32")}
                onCancel={cancelSwitch}
                onConfirm={confirmSwitch}
                open={Boolean(pendingSwitch)}
                title={t("sections.content.p33")}
            />
        </div>
    );
};
